import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type Premission from '@/utils/premissions/premission.type';
import { getUserData } from '@/utils/UserDataStore';

type Props = {
  children: React.ReactNode;
  prem?: Premission | Premission[]; // Single permission or array of permissions
  requiredRole?: string | string[]; // Single role or array of roles
};

export default function PagePremission({ children, prem, requiredRole }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const userData = getUserData();

  // Helper function to check if user has any of the required permissions
  const hasAnyPermission = useMemo(() => {
    if (!prem) return false;
    const perms = Array.isArray(prem) ? prem : [prem];
    return perms.some((p) => userData?.premissions.includes(p));
  }, [prem, userData?.premissions]);

  // Helper function to check if user has any of the required roles
  const hasAnyRole = useMemo(() => {
    if (!requiredRole || !userData?.job?.currentRole) return false;
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    return roles.includes(userData.job.currentRole);
  }, [requiredRole, userData?.job?.currentRole]);

  // Check if user has access (permission OR role)
  const hasAccess = useMemo(() => {
    // If neither permission nor role is specified, deny access
    if (!prem && !requiredRole) return false;

    // User has access if they have ANY of: required permission OR required role
    return hasAnyPermission || hasAnyRole;
  }, [hasAnyPermission, hasAnyRole, prem, requiredRole]);

  useEffect(() => {
    if (!hasAccess) {
      navigate('/not-auth', { state: { from: location }, replace: true });
    }
  }, [location, navigate, hasAccess, hasAnyPermission, hasAnyRole, userData?.job?.currentRole, requiredRole, prem]);

  return hasAccess ? children : null;
}
